import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Input from '@mui/material/Input';
import CircularProgress from '@mui/material/CircularProgress';
import { MdEdit, MdDelete, MdCheck, MdClose } from 'react-icons/md';

import { getThemes, createTheme, updateTheme, deleteTheme } from '../../api/admin';
import { useNotification } from '../../context/notification';
import { useGlobalData } from '../../context/globalData';
import { useAuth } from '../../context/auth';

function Users() {
  const [formData, setFormData] = useState([]);
  const { addNotification } = useNotification();
  const { globalData } = useGlobalData();
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [langueForm, setLangueForm] = useState(
    globalData?.langues
      .filter((langue) => auth.user.rights[langue.label])
      .map((langue) => ({ ...langue, inputValue: '', editValue: '', asChanged: false })) || [],
  );

  const handleOnChangeTheme = (key, value, themeID) => {
    const updatedState = formData.map((theme) => {
      if (theme.themeID === themeID) {
        return { ...theme, [key]: value };
      }
      return theme;
    });
    setFormData(updatedState);
  };

  const handleOnChangeInput = (key, value, id) => {
    const updatedState = langueForm.map((langue) => {
      if (langue.languageID === id) {
        return { ...langue, [key]: value, asChanged: key !== 'asChanged' };
      }
      return langue;
    });
    setLangueForm(updatedState);
  };

  const { isLoading: themeIsLoading } = useQuery('theme', getThemes, {
    onSuccess: (data) => {
      setFormData(data.map((theme) => ({ ...theme, isEditing: false })));
    },
  });

  const { mutate: addTheme, isLoading: addThemeIsLoading } = useMutation((data) => createTheme(data), {
    onSuccess: (data) => {
      addNotification({ message: data.message });
      handleOnChangeInput('asChanged', false, data.languageID);
      document.getElementById(`select_${data.languageID}`).value = '';
      queryClient.invalidateQueries('theme');
    },
    onError: (err) => {
      addNotification({ message: `Une erreur est survenue: ${err.message}`, severity: 'error' });
    },
  });

  const { mutate: editTheme, isLoading: updateThemeIsLoading } = useMutation((data) => updateTheme(data), {
    onSuccess: (data) => {
      handleOnChangeTheme('isEditing', false, data.themeID);
      queryClient.invalidateQueries('theme');
      addNotification({ message: data.message });
    },
    onError: (err) => {
      addNotification({ message: `Une erreur est survenue: ${err.message}`, severity: 'error' });
    },
  });

  const { mutate: removeTheme, isLoading: deleteThemeIsLoading } = useMutation((data) => deleteTheme(data), {
    onSuccess: (data) => {
      queryClient.invalidateQueries('theme');
      addNotification({ message: data.message });
    },
    onError: (err) => {
      addNotification({ message: `Une erreur est survenue: ${err.message}`, severity: 'error' });
    },
  });

  return (
    <section className="flex justify-center p-8 items-center space-x-6 authForm flex-wrap">
      {themeIsLoading ? (
        <CircularProgress />
      ) : (
        langueForm.map((langue) => (
          <div key={`theme_${langue.label}`} className="bg-black p-8 space-y-4 rounded h-fit">
            <span>{langue.label?.toUpperCase()}</span>
            <div className=" max-h-96 overflow-auto p-2 rounded">
              {formData
                .filter((theme) => theme.languageID === langue.languageID)
                .sort((a, b) => (a.value < b.value ? -1 : 1))
                .map((theme) => (
                  <ul
                    key={`theme_${theme.themeID}`}
                    className="flex items-cente justify-between p-1 space-x-2 hover:bg-zinc-800 duration-300 list-disc list-inside"
                  >
                    {theme.isEditing ? (
                      <input
                        className="bg-transparent border-b border-white w-full outline-none"
                        defaultValue={theme.value}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            editTheme({ value: langue.editValue, languageID: langue.languageID, themeID: theme.themeID });
                          }
                        }}
                        type="text"
                        onChange={(e) => handleOnChangeInput('editValue', e.target.value, langue.languageID)}
                      />
                    ) : (
                      <li className="max-w-xs">{theme.value}</li>
                    )}
                    <div className="flex items-center space-x-2">
                      {theme.isEditing ? (
                        <>
                          {updateThemeIsLoading ? (
                            <CircularProgress size={13} />
                          ) : (
                            <button
                              className="success"
                              type="button"
                              onClick={() =>
                                editTheme({ value: langue.editValue, languageID: langue.languageID, themeID: theme.themeID })
                              }
                            >
                              <MdCheck title="Valider" />
                            </button>
                          )}
                          {!updateThemeIsLoading && (
                            <button
                              className="danger"
                              onClick={() => handleOnChangeTheme('isEditing', false, theme.themeID)}
                              type="button"
                              title="Annuler"
                            >
                              <MdClose />
                            </button>
                          )}
                        </>
                      ) : (
                        <>
                          {!deleteThemeIsLoading && (
                            <button
                              className="focus"
                              title="Modifier"
                              type="button"
                              onClick={() => handleOnChangeTheme('isEditing', true, theme.themeID)}
                            >
                              <MdEdit />
                            </button>
                          )}
                          {deleteThemeIsLoading ? (
                            <CircularProgress size={13} />
                          ) : (
                            <button title="Supprimer" onClick={() => removeTheme(theme.themeID)} className="danger" type="button">
                              <MdDelete />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </ul>
                ))}
            </div>
            <FormControl fullWidth variant="standard">
              <InputLabel>Thème</InputLabel>
              <Input
                dir={langue.label === 'ar' ? 'rtl' : 'ltr'}
                autoComplete="off"
                id={`select_${langue.languageID}`}
                type="text"
                onChange={(e) => handleOnChangeInput('inputValue', e.target.value, langue.languageID)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    addTheme({ value: langue.inputValue, languageID: langue.languageID });
                    e.target.value = '';
                  }
                }}
                size="small"
              />
            </FormControl>
            <button
              type="button"
              disabled={!langue.asChanged}
              className="px-4 py-2 mt-8 rounded bg-zinc-900 hover:bg-blue-500 duration-500 disabled:opacity-50 w-24 disabled:hover:bg-zinc-900"
              onClick={() => addTheme({ value: langue.inputValue, languageID: langue.languageID })}
            >
              {addThemeIsLoading ? <CircularProgress size={13} /> : 'Ajouter'}
            </button>
          </div>
        ))
      )}
    </section>
  );
}

Users.propTypes = {};

export default Users;
